import { produce } from 'immer';
import type { DeploymentHistoryEntry, SnowflakeTableFqn, TableType } from 'app/lib/types/server';
import type { TypedReduxActionOutcomes } from 'app/store/storeTypes';
import { extractError } from 'app/lib/error';
import { toStringFqn } from 'app/lib/utils';
import {
    CLOSE_DEPLOYMENT_HISTORY_DIALOG,
    CLOSE_RESTORE_TRANSFORMATION_DEPLOYMENT_DIALOG,
    LOAD_DEPLOYMENT_HISTORY,
    OPEN_DEPLOYMENT_HISTORY_DIALOG,
    OPEN_RESTORE_TRANSFORMATION_DEPLOYMENT_DIALOG,
    RESTORE_TRANSFORMATION_DEPLOYMENT
} from './types.js';

export type DeploymentHistoryStateSliceType = {
    isOpen: boolean;
    isLoading: boolean;
    entries: DeploymentHistoryEntry[];
    entriesByFqn: Record<string, DeploymentHistoryEntry[]>;
    error: null | string;
    fullyQualifiedIdentifier: null | SnowflakeTableFqn;
    sourceType: null | TableType;
    restore: {
        isRestoring: boolean;
        isOpen: boolean;
        error: null | Record<string, string>;
        version: null | number;
    };
    selectedDeploymentVersion: null | number;
};
export const INITIAL_STATE: DeploymentHistoryStateSliceType = {
    isOpen: false,
    isLoading: false,
    entries: [],
    entriesByFqn: {},
    error: null,
    fullyQualifiedIdentifier: null,
    sourceType: null,
    restore: {
        isRestoring: false,
        isOpen: false,
        error: null,
        version: null
    },
    selectedDeploymentVersion: null
};

export const deploymentHistoryReducer = produce((draft, action: TypedReduxActionOutcomes) => {
    switch (action.type) {
        case OPEN_DEPLOYMENT_HISTORY_DIALOG:
            draft.isOpen = true;
            draft.fullyQualifiedIdentifier = action.payload.fullyQualifiedIdentifier;
            draft.sourceType = action.payload.sourceType;
            draft.error = null;
            draft.entries = [];
            draft.selectedDeploymentVersion = action.payload.selectedDeploymentVersion;
            break;
        case CLOSE_DEPLOYMENT_HISTORY_DIALOG:
            draft.isOpen = false;
            draft.error = null;
            draft.entries = [];
            break;
        case LOAD_DEPLOYMENT_HISTORY.STARTED:
            draft.isLoading = true;
            draft.entries = [];
            break;
        case LOAD_DEPLOYMENT_HISTORY.SUCCEEDED: {
            const { entries } = action.payload;
            const { database, schema, table } = action.params;
            const fqn: SnowflakeTableFqn = { database, schema, table };

            draft.entries = entries;
            draft.entriesByFqn[toStringFqn(fqn)] = entries;
            draft.isLoading = false;
            break;
        }
        case LOAD_DEPLOYMENT_HISTORY.FAILED: {
            draft.error = extractError(action.payload);
            draft.isLoading = false;
            break;
        }
        case OPEN_RESTORE_TRANSFORMATION_DEPLOYMENT_DIALOG:
            draft.restore.version = action.payload.version;
            draft.isOpen = false;
            draft.restore.isOpen = true;
            break;
        case CLOSE_RESTORE_TRANSFORMATION_DEPLOYMENT_DIALOG:
            draft.isOpen = true;
            draft.restore = INITIAL_STATE.restore;
            break;
        case RESTORE_TRANSFORMATION_DEPLOYMENT.STARTED: {
            draft.restore.isRestoring = true;
            draft.restore.error = null;
            break;
        }
        case RESTORE_TRANSFORMATION_DEPLOYMENT.PROGRESS: {
            draft.restore.isOpen = false;
            draft.isOpen = true;
            break;
        }
        case RESTORE_TRANSFORMATION_DEPLOYMENT.SUCCEEDED: {
            draft.restore.isRestoring = false;
            draft.restore.version = null;
            draft.restore.isOpen = false;
            draft.isOpen = false;
            break;
        }
        case RESTORE_TRANSFORMATION_DEPLOYMENT.FAILED: {
            draft.restore.error = extractError(action.payload);
            draft.restore.isRestoring = false;
            break;
        }
        default:
    }
}, INITIAL_STATE);
